<template>
  <vc-date-picker ref="vc"
                  :value="dateObj"
                  :popover="{ visibility: 'click', placement: 'bottom-start' }"
                  :masks="masks"
                  :is-required="false"
                  @input="handleInput"
                  @popoverWillShow="onPopoverWillShow"
                  @popoverWillHide="onPopoverWillHide">
    <template v-slot="{ inputEvents }">
      <input class="date-picker-input"
             cy="date-picker-input"
             type="text"
             readonly
             :value="formattedDisplay"
             :placeholder="placeholder"
             :disabled="disabled"
             v-on="inputEvents">
    </template>
  </vc-date-picker>
</template>

<script>
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/

// Module-level registry of DatePicker instances whose popover is currently
// open. Used to enforce "only one open at a time" UX across all instances.
const openRegistry = new Set()

function pad (n) {
  return String(n).padStart(2, '0')
}

export default {
  name: 'DatePicker',
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'zz/ll/aaaa'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      masks: {
        input: 'DD/MM/YYYY'
      }
    }
  },
  computed: {
    parsed () {
      const m = ISO_RE.exec(this.value)
      if (!m) return null
      return { y: +m[1], mo: +m[2], d: +m[3] }
    },
    dateObj () {
      if (!this.parsed) return null
      return new Date(this.parsed.y, this.parsed.mo - 1, this.parsed.d)
    },
    formattedDisplay () {
      if (!this.parsed) return ''
      return `${pad(this.parsed.d)}/${pad(this.parsed.mo)}/${this.parsed.y}`
    }
  },
  beforeDestroy () {
    openRegistry.delete(this)
  },
  methods: {
    handleInput (date) {
      if (!date) {
        this.$emit('input', '')
        return
      }
      const iso = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
      this.$emit('input', iso)
    },
    onPopoverWillShow () {
      openRegistry.forEach((other) => {
        if (other !== this && other.$refs && other.$refs.vc) {
          try { other.$refs.vc.hidePopover() } catch (e) { /* noop */ }
        }
      })
      openRegistry.add(this)
    },
    onPopoverWillHide () {
      openRegistry.delete(this)
    }
  }
}
</script>

<style lang="less">
@import "../../styles/var";

.date-picker-input {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  font-size: 1.5rem;
  border: 1px solid #C3C3C3;
  border-radius: 6px;
  background-color: white;
  color: @dark-gray;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #999;
  }

  &:hover:not(:disabled) {
    border-color: @blue;
  }

  &:focus {
    outline: none;
    border-color: @blue;
    box-shadow: 0 0 0 2px rgba(41, 171, 226, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
